<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import kanbanClient from 'dashboard/api/kanban/kanbanClient';
import { MEDIA_TYPES } from 'dashboard/components-next/message/constants';
import Media from 'dashboard/components-next/SharedAttachments/Media.vue';
import Files from 'dashboard/components-next/SharedAttachments/Files.vue';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  deal: { type: Object, required: true },
  active: { type: Boolean, default: true },
});
const { t } = useI18n();
const router = useRouter();
const { accountId, accountScopedRoute } = useAccount();
const { run, abort, isPending } = useAbortableRequest();
const attachments = ref([]);
const nextBeforeId = ref(null);
const loaded = ref(false);
const error = ref(false);
const selectedAttachment = ref(null);
const showGallery = ref(false);
const clear = () => {
  attachments.value = [];
  nextBeforeId.value = null;
  loaded.value = false;
  error.value = false;
  selectedAttachment.value = null;
  showGallery.value = false;
};
const load = async (more = false) => {
  const beforeId = more ? nextBeforeId.value : null;
  if (!more) clear();
  error.value = false;
  await run(async signal => {
    try {
      const { data } = await kanbanClient.getDealAttachments(
        props.deal.kanban_funnel_id,
        props.deal.id,
        beforeId,
        { signal }
      );
      if (signal.aborted) return;
      attachments.value = more
        ? [...attachments.value, ...data.payload]
        : data.payload;
      nextBeforeId.value = data.next_before_id;
      loaded.value = true;
    } catch (failure) {
      if (signal.aborted) return;
      if ([401, 403, 404].includes(failure.response?.status)) clear();
      error.value = true;
    }
  });
};
watch(
  [
    accountId,
    () => props.deal.id,
    () => props.deal.kanban_funnel_id,
    () => props.deal.lock_version,
    () => props.active,
  ],
  () => {
    abort();
    clear();
    if (props.active) load();
  },
  { immediate: true }
);
const mediaAttachments = computed(() =>
  attachments.value
    .filter(item => item.data_url && MEDIA_TYPES.includes(item.file_type))
    .sort((a, b) => (b.created_at || 0) - (a.created_at || 0))
);
const unavailable = computed(() =>
  attachments.value.filter(item => item.unavailable)
);
const selectMedia = attachment => {
  selectedAttachment.value = attachment;
  showGallery.value = true;
};
const openFile = attachment => {
  if (attachment.data_url)
    window.open(attachment.data_url, '_blank', 'noopener,noreferrer');
};
const openMessage = attachment => {
  const route = accountScopedRoute('inbox_conversation', {
    conversation_id: attachment.conversation_id,
  });
  const { href } = router.resolve({
    ...route,
    query: { messageId: attachment.message_id },
  });
  window.open(href, '_blank', 'noopener,noreferrer');
};
</script>

<template>
  <section
    :aria-label="t('KANBAN.MEDIA.TITLE')"
    :aria-busy="isPending"
    class="flex flex-col gap-3"
  >
    <div class="flex items-center justify-between gap-2">
      <h3 class="m-0 text-sm font-semibold">{{ t('KANBAN.MEDIA.TITLE') }}</h3>
      <Button
        variant="ghost"
        size="sm"
        icon="i-lucide-refresh-cw"
        :disabled="isPending || !active"
        :aria-label="t('KANBAN.MEDIA.REFRESH')"
        @click="load(false)"
      />
    </div>
    <p class="m-0 text-xs text-n-slate-10">{{ t('KANBAN.MEDIA.SCOPE') }}</p>
    <Media
      :attachments="attachments"
      :peek-limit="12"
      show-jump-to-message
      @select="selectMedia"
      @jump-to-message="openMessage"
    />
    <Files
      :attachments="attachments"
      :peek-limit="6"
      show-jump-to-message
      @select="openFile"
      @jump-to-message="openMessage"
    />
    <ul v-if="unavailable.length" class="m-0 list-none space-y-2 p-0">
      <li
        v-for="attachment in unavailable"
        :key="attachment.id"
        class="text-xs text-n-slate-11"
      >
        <p class="m-0">
          {{ attachment.file_name || t('KANBAN.MEDIA.UNAVAILABLE') }}
        </p>
        <p class="m-0">
          {{ attachment.inbox_name }} · #{{ attachment.conversation_id }}
        </p>
        <Button
          variant="ghost"
          size="sm"
          :label="t('KANBAN.MEDIA.OPEN_MESSAGE')"
          @click="openMessage(attachment)"
        />
      </li>
    </ul>
    <Spinner v-if="isPending" />
    <p v-if="error" role="alert" class="text-xs text-n-ruby-11">
      {{ t('KANBAN.MEDIA.ERROR') }}
    </p>
    <p
      v-else-if="loaded && !attachments.length"
      class="m-0 text-xs text-n-slate-10"
    >
      {{ t('KANBAN.MEDIA.EMPTY') }}
    </p>
    <div
      v-if="attachments.length"
      class="flex items-center justify-between gap-2"
    >
      <span class="text-xs text-n-slate-10">{{
        t('KANBAN.MEDIA.LOADED', { count: attachments.length })
      }}</span>
      <Button
        v-if="nextBeforeId"
        variant="ghost"
        size="sm"
        :disabled="isPending"
        :label="t('KANBAN.MEDIA.MORE')"
        @click="load(true)"
      />
    </div>
    <GalleryView
      v-if="showGallery && selectedAttachment"
      v-model:show="showGallery"
      :attachment="selectedAttachment"
      :all-attachments="mediaAttachments"
      auto-play
      @close="showGallery = false"
    />
  </section>
</template>
